<template>
    <!-- Desktop Modal -->
    <UiModal
        v-if="!isMobile"
        :is-open="isOpen"
        title="Add custom renewal date"
        @close="closeModal"
        width="600px"
    >
        <template #subtitle>
            <div class="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                {{ selectedBenefit?.benefit?.benefit_title }}
                <span class="text-xs">•</span>
                {{ selectedBenefit?.card?.title }}
                {{
                    selectedBenefit?.connected_account_last_four
                        ? `(${selectedBenefit?.connected_account_last_four})`
                        : ''
                }}
            </div>
        </template>
        <template #content>
            <div class="w-full">
                <p class="text-sm text-slate-600 dark:text-slate-300 mb-4">
                    Set a custom renewal date for this benefit. The benefit will
                    reset on this date each year.
                </p>
                <div class="mb-4">
                    <label
                        class="block text-sm font-medium text-slate-700 mb-1"
                    >
                        Renewal Date
                    </label>
                    <div class="flex gap-3">
                        <select
                            v-model="selectedMonth"
                            class="flex-1 block w-full px-3 py-1.5 border border-slate-200 dark:border-slate-700 rounded text-sm font-normal focus:ring-0 focus:border-slate-800 hover:border-slate-800 transition-colors"
                        >
                            <option value="" disabled>Month</option>
                            <option
                                v-for="(month, index) in months"
                                :key="index"
                                :value="index + 1"
                            >
                                {{ month }}
                            </option>
                        </select>
                        <select
                            v-model="selectedDay"
                            class="w-24 block px-3 py-1.5 border border-slate-200 dark:border-slate-700 rounded text-sm font-normal focus:ring-0 focus:border-slate-800 hover:border-slate-800 transition-colors"
                        >
                            <option value="" disabled>Day</option>
                            <option
                                v-for="day in daysInSelectedMonth"
                                :key="day"
                                :value="day"
                            >
                                {{ day }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex justify-between py-4">
                <button
                    v-if="selectedBenefit?.user_override?.reset_date_override"
                    type="button"
                    class="btn-base btn-danger"
                    :disabled="loading"
                    @click="showDeleteConfirmation = true"
                >
                    Delete
                </button>
                <div v-else></div>
                <div class="flex gap-3">
                    <button
                        type="button"
                        class="btn-base btn-white"
                        @click="closeModal"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn-base btn-dark"
                        :disabled="loading || !isFormValid"
                        @click="handleSave"
                    >
                        <span v-if="loading">Saving...</span>
                        <span v-else>Save</span>
                    </button>
                </div>
            </div>
        </template>
    </UiModal>

    <!-- Mobile Modal -->
    <UiModalMobile v-else :is-open="isOpen" @close="closeModal">
        <template #header>
            <div class="flex flex-col w-full">
                <div
                    class="flex items-center justify-between bg-white dark:bg-slate-800 z-10 rounded-t-2xl w-full"
                >
                    <h2 class="text-xl font-medium text-slate-800 dark:text-slate-100">
                        Custom Renewal Date
                    </h2>
                    <button @click="closeModal" class="btn-base btn-icon !m-0">
                        <Icon icon="x" class="text-slate-500 dark:text-slate-400" />
                    </button>
                </div>
                <div class="text-sm text-slate-500 dark:text-slate-400">
                    {{ selectedBenefit?.benefit?.benefit_title }}
                </div>
            </div>
        </template>
        <template #content>
            <div class="w-full pb-20">
                <p class="text-sm text-slate-600 dark:text-slate-300 mb-4">
                    Set a custom renewal date for this benefit. The benefit will
                    reset on this date each year.
                </p>
                <div class="mb-4">
                    <label
                        class="block text-sm font-medium text-slate-700 mb-1"
                    >
                        Renewal Date
                    </label>
                    <div class="flex gap-3">
                        <select
                            v-model="selectedMonth"
                            class="flex-1 block w-full px-3 py-1.5 border border-slate-200 dark:border-slate-700 rounded text-sm font-normal focus:ring-0 focus:border-slate-800 hover:border-slate-800 transition-colors"
                        >
                            <option value="" disabled>Month</option>
                            <option
                                v-for="(month, index) in months"
                                :key="index"
                                :value="index + 1"
                            >
                                {{ month }}
                            </option>
                        </select>
                        <select
                            v-model="selectedDay"
                            class="w-24 block px-3 py-1.5 border border-slate-200 dark:border-slate-700 rounded text-sm font-normal focus:ring-0 focus:border-slate-800 hover:border-slate-800 transition-colors"
                        >
                            <option value="" disabled>Day</option>
                            <option
                                v-for="day in daysInSelectedMonth"
                                :key="day"
                                :value="day"
                            >
                                {{ day }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex flex-col gap-3">
                <div class="flex gap-3">
                    <button
                        type="button"
                        class="flex-1 btn-base btn-white"
                        @click="closeModal"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="flex-1 btn-base btn-primary"
                        :disabled="loading || !isFormValid"
                        @click="handleSave"
                    >
                        <span v-if="loading">Saving...</span>
                        <span v-else>Save</span>
                    </button>
                </div>
                <button
                    v-if="selectedBenefit?.user_override?.reset_date_override"
                    type="button"
                    class="btn-base btn-danger"
                    :disabled="loading"
                    @click="showDeleteConfirmation = true"
                >
                    Delete
                </button>
            </div>
        </template>
    </UiModalMobile>

    <!-- Delete Confirmation Modal -->
    <UiModal
        :is-open="showDeleteConfirmation"
        title="Delete saved renewal date?"
        width="400px"
        @close="showDeleteConfirmation = false"
    >
        <template #content>
            <p class="text-sm text-slate-600 dark:text-slate-300">
                Deleting your saved renewal date will reset the benefit's
                renewal date to My10x's default data.
            </p>
        </template>
        <template #footer>
            <div class="flex justify-end gap-3 py-4">
                <button
                    type="button"
                    class="btn-base btn-white"
                    @click="showDeleteConfirmation = false"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn-base btn-danger"
                    :disabled="loading"
                    @click="confirmDelete"
                >
                    <span v-if="loading">Deleting...</span>
                    <span v-else>Delete</span>
                </button>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import { useIsMobile } from '@/composables/useIsMobile';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import Icon from '@/components/ui/icon/icon.vue';
import dayjs from 'dayjs';

const { isMobile } = useIsMobile();

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close']);

const store = useCardBenefitsStore();
const { selectedBenefit } = storeToRefs(store);

const selectedMonth = ref('');
const selectedDay = ref('');
const loading = ref(false);
const showDeleteConfirmation = ref(false);

const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const daysInSelectedMonth = computed(() => {
    if (!selectedMonth.value) return 31;
    const daysPerMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return daysPerMonth[selectedMonth.value - 1];
});

// Computed to check if form is valid
const isFormValid = computed(() => {
    return selectedMonth.value && selectedDay.value;
});

// Pre-fill date when modal opens
watch(
    () => props.isOpen,
    (isOpen) => {
        if (isOpen) {
            const existingDate =
                selectedBenefit.value?.user_override?.reset_date_override;
            if (existingDate) {
                const date = dayjs(existingDate);
                selectedMonth.value = date.month() + 1;
                selectedDay.value = date.date();
            } else {
                const periodEndDate = selectedBenefit.value?.period_end_date;
                if (periodEndDate) {
                    const date = dayjs(periodEndDate);
                    selectedMonth.value = date.month() + 1;
                    selectedDay.value = date.date();
                } else {
                    selectedMonth.value = '';
                    selectedDay.value = '';
                }
            }
        }
    }
);

// Adjust day if it exceeds the days in the newly selected month
watch(selectedMonth, () => {
    if (selectedDay.value > daysInSelectedMonth.value) {
        selectedDay.value = daysInSelectedMonth.value;
    }
});

const closeModal = () => {
    selectedMonth.value = '';
    selectedDay.value = '';
    loading.value = false;
    showDeleteConfirmation.value = false;
    emit('close');
};

const updateSelectedBenefit = () => {
    const updatedBenefit = store.benefits.find(
        (benefit) =>
            benefit.benefit.card_benefit_id ===
                selectedBenefit.value?.benefit?.card_benefit_id &&
            benefit.connected_account_id ===
                selectedBenefit.value?.connected_account_id
    );
    if (updatedBenefit) {
        selectedBenefit.value = updatedBenefit;
    }
};

const handleSave = async () => {
    if (!isFormValid.value) return;

    // Build a date string using current year (year doesn't matter for renewal)
    const year = dayjs().year();
    const dateStr = `${year}-${String(selectedMonth.value).padStart(2, '0')}-${String(selectedDay.value).padStart(2, '0')}`;

    loading.value = true;
    try {
        const success = await store.saveUserOverride({
            card_benefit_id: selectedBenefit.value?.benefit?.card_benefit_id,
            connected_account_id: selectedBenefit.value?.connected_account_id,
            reset_date_override: dateStr,
        });
        if (success) {
            updateSelectedBenefit();
            closeModal();
        }
    } catch (error) {
        console.error('Failed to save renewal date override:', error);
    } finally {
        loading.value = false;
    }
};

const confirmDelete = async () => {
    loading.value = true;
    try {
        const success = await store.deleteUserOverride({
            card_benefit_id: selectedBenefit.value?.benefit?.card_benefit_id,
            connected_account_id: selectedBenefit.value?.connected_account_id,
        });
        if (success) {
            updateSelectedBenefit();
            closeModal();
        }
    } catch (error) {
        console.error('Failed to delete renewal date override:', error);
    } finally {
        loading.value = false;
    }
};
</script>
